"use client";

import { format } from "date-fns";
import { type IDateValidator, parseDateValidators } from "forms/helpers/parseDateValidators";
import { Calendar as CalendarIcon } from "lucide-react";
import { forwardRef } from "react";

import { useFormField } from "uiShadcn/form";
import { cn } from "utils/helpers/cn";

export interface DatePickerProps {
    className?: string;
    dataTestId?: string;
    onValueChange: (value: string | null) => void;
    validators?: IDateValidator;
    required?: boolean;
    value?: string | null;
    label?: string;
}

const NativeDatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
    ({ className, dataTestId, onValueChange, required = false, value, label, validators }, ref) => {
        const { error, formItemId, name } = useFormField();
        const { minDate, maxDate } = parseDateValidators(validators);

        const handleChange = (dateString: string) => {
            if (!dateString) {
                onValueChange(null);
                return;
            }

            const newDate = new Date(dateString);
            if (Number.isNaN(newDate.getTime())) {
                return;
            }

            onValueChange(dateString);
        };
        const min = minDate ? format(minDate, "yyyy-MM-dd") : undefined;
        const max = maxDate ? format(maxDate, "yyyy-MM-dd") : undefined;

        const dateValue = value ? new Date(value) : null;
        const formattedValue = dateValue && !Number.isNaN(dateValue.getTime()) ? format(dateValue, "yyyy-MM-dd") : "";

        return (
            <div className="relative">
                <CalendarIcon
                    className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 text-muted-foreground dark:text-foreground"
                    aria-hidden="true"
                />
                <input
                    ref={ref}
                    type="date"
                    id={formItemId}
                    name={name}
                    value={formattedValue}
                    onChange={(e) => handleChange(e.target.value)}
                    min={min}
                    max={max}
                    className={cn(
                        "relative rounded-md border border-input bg-background px-2 py-2 text-sm uppercase ring-offset-background sm:w-auto",
                        "file:border-0 file:bg-transparent file:font-medium file:text-sm",
                        "placeholder:text-muted-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "pl-9",
                        !!error && "border-destructive",
                        !dateValue && "text-muted-foreground",
                        className,
                    )}
                    data-test-id={dataTestId ?? "date-picker"}
                    required={required}
                    aria-label={label}
                />
            </div>
        );
    },
);

export default NativeDatePicker;
